import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Paper,
    Typography,
    TextField,
} from "@mui/material";
import { useAuth, useNavigation, useLoading, useSnackbar } from "../../../Context/Contexts";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { TerminalViewModel } from "../../../Types/ExportTypes";
import CenteredListCard from "../../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../../components/List/ListCardHeader";
import useCachedListQuery from "../../../hooks/useCachedListQuery";
import { useTranslation } from "react-i18next";
import LoadingSection from "../../../components/LoadingSection";
import DeleteIcon from "@mui/icons-material/Delete";
import { ShopAutoComplete } from "../../../components/AutoComplete/AutoCompletes";
import { buildQueryFilter } from "../../../Utils/QueryFilterUtils";
import ResponsivePagination from "../../../components/ResponsivePagination";

const TerminalList: React.FC = () => {
    const [items, setItems] = useState<TerminalViewModel[]>([]);
    const { t } = useTranslation();
    const [totalCount, setTotalCount] = useState(0);
    const [query, setQuery] = useCachedListQuery({
        page: 0,
        pageSize: 10,
        filter: "",
        filters: [],
    });
    const [filter, setFilter] = useState<string>("");
    const [filterProperty, setFilterProperty] = useState<string>("");
    const [boolFilter, setBoolFilter] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);

    const { authService } = useAuth();
    const { navigate } = useNavigation();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const fetchItems = async () => {
        setLoading(true);
        try {
            const response = await authService.post<{ items: TerminalViewModel[]; count: number }>(
                "/settingsApi/GetTerminalList",
                query
            );
            setItems(response.items);
            setTotalCount(response.count);
        } catch (error: any) {
            showSnackbar(`${t("common.failedToFetchEntries")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, [query]);

    useEffect(() => {
        if (loading) showLoading();
        else hideLoading();
    }, [loading, showLoading, hideLoading]);

    useEffect(() => {
        setFilter("");
        setBoolFilter(false);
    }, [filterProperty]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        var localFilterProperty = filterProperty;
        //if (!filter) return;
        if (!localFilterProperty?.trim() && filter?.trim()) {
            localFilterProperty = "Default";
        }

        if (
            localFilterProperty?.trim() &&
            (localFilterProperty === "ext_Active" || localFilterProperty === "ext_SelfCheckout" || filter?.trim())
        ) {
            const value =
                localFilterProperty === "ext_Active" || localFilterProperty === "ext_SelfCheckout"
                    ? boolFilter.toString()
                    : filter;
            const newFilter = buildQueryFilter(localFilterProperty, value);
            const isDuplicate = query.filters?.some(
                (f) => f.property === newFilter.property && f.value === newFilter.value
            );
            if (isDuplicate) return;
            const newFilters = [...(query.filters ?? []), newFilter];
            setQuery({ ...query, filters: newFilters, page: 0, filter: "" });
        } else {
            setQuery({ ...query, filter, page: 0 });
        }

        setFilter("");
        setFilterProperty("");
        setBoolFilter(false);
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handleSelect = (id?: number | null) => {
        navigate({ path: "/settings/terminal/edit", params: { id } });
    };

    const handleCreate = () => {
        navigate({ path: "/settings/terminal/edit", params: {} });
    };

    return (
        <CenteredListCard title={t("common.terminal")}>
  
                <Box sx={{ marginBottom: 2 }}>
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={handleCreate}
                    >
                        {t("common.create")}
                    </Button>
                </Box>
                <Box
                    component="form"
                    onSubmit={handleSearchSubmit}
                    sx={{
                        display: "flex",
                        gap: 2,
                        marginBottom: 2,
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={5} lg={2}>
                            <TextField
                                select
                                label={t("common.field")}
                                value={filterProperty}
                                onChange={(e) => setFilterProperty(e.target.value)}
                                SelectProps={{ native: true }}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            >
                                <option value="">{t("common.default")}</option>
                                <option value="ext_ShopNo">{t("common.shop")}</option>
                                <option value="ext_Active">{t("common.active")}</option>
                                <option value="ext_SelfCheckout">Self Checkout</option>
                                <option value="ext_TerminalNo">{t("common.terminalNo")}</option>
                            </TextField>
                        </Grid>
                        <Grid item xs={7} lg={7}>
                            {(() => {
                                switch (filterProperty) {
                                    case "ext_ShopNo":
                                        return (
                                            <ShopAutoComplete onChange={(o) => setFilter(o?.value || "")} />
                                        );
                                    case "ext_Active":
                                    case "ext_SelfCheckout":
                                        return (
                                            <TextField
                                                select
                                                label={t("common.value")}
                                                variant="outlined"
                                                fullWidth
                                                onChange={(e) => setBoolFilter(e.target.value === "true")}
                                                value={boolFilter.toString()}
                                                disabled={loading}
                                                SelectProps={{ native: true }}
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                            >
                                                <option value="true">{t("common.yes")}</option>
                                                <option value="false">{t("common.no")}</option>
                                            </TextField>
                                        );
                                    case "ext_TerminalNo":
                                        return (
                                            <TextField
                                                label={t("common.terminalNo")}
                                                variant="outlined"
                                                type="number"
                                                fullWidth
                                                onChange={(event) => setFilter(event.target.value)}
                                                value={filter}
                                                disabled={loading}
                                            />
                                        );
                                    default:
                                        return (
                                            <TextField
                                                label={t("common.search")}
                                                variant="outlined"
                                                fullWidth
                                                onChange={(event) => setFilter(event.target.value)}
                                                value={filter}
                                                disabled={loading}
                                            />
                                        );
                                }
                            })()}
                        </Grid>
                        <Grid item xs={12} lg={3}>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={loading}
                            >
                                {t("common.search")}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                    {query.filters?.map((f, index) => (
                        <Chip
                            key={index}
                            label={`${f.property === "Default" ? t("common.default") : f.property}: ${f.value}`}
                            onDelete={() => {
                                const newFilters = query.filters?.filter((_, i) => i !== index);
                                setQuery({ ...query, filters: newFilters, page: 0 });
                            }}
                            deleteIcon={<DeleteIcon />}
                        />
                    ))}
                </Box>


                <LoadingSection loading={loading}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                        {items.map((item) => (
                            <Grid item
                                xs={12}
                                sm={items.length === 1 ? 12 : items.length === 2 ? 6 : 6}
                                md={items.length === 1 ? 12 : items.length === 2 ? 6 : 4}
                                key={item.id}>
                                <Card
                                    onClick={() => handleSelect(item.id)}
                                    sx={{
                                        cursor: "pointer",
                                        transition: "box-shadow 0.2s ease-in-out",
                                        "&:hover": {
                                            boxShadow: 4,
                                            backgroundColor: "secondary.main",
                                        },
                                    }}
                                >
                                    <CardContent>
                                        <ListCardHeader
                                            icon={<PointOfSaleIcon fontSize="small" color="action" />}
                                            title={item.terminalNo}
                                            status={{
                                                label: item.active ? t("common.active") : t("common.inactive"),
                                                color: item.active ? "success" : "default",
                                                icon: item.active ? (
                                                    <CheckCircleIcon fontSize="small" />
                                                ) : (
                                                    <CancelIcon fontSize="small" />
                                                ),
                                            }}
                                        />
                                        <Typography variant="body2" color="textSecondary">
                                            {t("common.shop")}: {item.shopNo} -  {item.shopName}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {t("common.active")}: {item.active ? t("common.yes") : t("common.no")}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Self Checkout: {item.selfCheckout ? t("common.yes") : t("common.no")}
                                        </Typography>

                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                        {items.length === 0 && (
                            <Grid item xs={12}>
                                <Typography align="center">{t("common.noEntriesFound")}</Typography>
                            </Grid>
                        )}
                    </Grid>
                </LoadingSection>

                {/* Pagination */}
                <ResponsivePagination

                    page={query.page}

                    pageSize={query.pageSize}

                    totalCount={totalCount}

                    onPageChange={handlePageChange}

                />
      
        </CenteredListCard>

    );
};

export default TerminalList;
